import { useContext, useState, useEffect } from 'react';
import Scrollbar from 'src/components/Scrollbar';
import { SidebarContext } from 'src/Context/SidebarContext';
import apiFetch from 'src/Common/api/auth/Auth';
import { useCurrentPlan } from 'src/Common/hooks/useCurrentPlan';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Drawer,
  alpha,
  styled,
  Divider,
  useTheme,
  Button,
  lighten,
  darken,
  CircularProgress
} from '@mui/material';
import SidebarMenu from './SidebarMenu';
import { IEmployeeProfile } from 'src/Common/Interfaces/Profile/IEmployeeProfile';
import { Link as RouterLink } from 'react-router-dom';
import { isBillingRestricted } from 'src/Common/navigation/billingRestriction';

const SidebarWrapper = styled(Box)(
  ({ theme }) => `
    width: ${theme.sidebar.width};
    min-width: ${theme.sidebar.width};
    color: ${(theme as any).sidebar.textColor};
    position: relative;
    z-index: 7;
    height: 100%;
    transition: background 0.3s ease, color 0.3s ease;
`
);

function Sidebar() {
  const { t } = useTranslation();
  const { sidebarToggle, toggleSidebar } = useContext(SidebarContext);
  const theme = useTheme();
  const [currentPlan, setCurrentPlan] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [employeeProfile, setEmployeeProfile] = useState<IEmployeeProfile | null>(null);

  // Fetch employee profile from localStorage
  useEffect(() => {
    const storedEmployeeProfile = localStorage.getItem("employeeProfile");
    if (storedEmployeeProfile) {
      try {
        const parsedProfile = JSON.parse(storedEmployeeProfile) as IEmployeeProfile;
        setEmployeeProfile(parsedProfile);
      } catch (error) {
        console.error("Error parsing employee profile:", error);
      }
    }
  }, []);

  const { currentPlan: planFromHook, loading: loadingPlan } = useCurrentPlan(employeeProfile?.companyId);

  useEffect(() => {
    if (planFromHook !== undefined) setCurrentPlan(planFromHook);
    setLoading(!!loadingPlan);
  }, [planFromHook, loadingPlan]);

  const getButtonConfig = () => {
    if (!currentPlan) {
      return { 
        text: t('sidebar.upgradeToPro'), 
        href: '/subscriptions/upgrade', 
        color: 'warning' 
      };
    }

    // Use correct property names from API response
    const planName = currentPlan.packageName;
    const isTrial = currentPlan.planDetails.isTrial;
    const daysLeft = currentPlan.daysRemaining;

    if (isTrial) {
      return {
        text: t('sidebar.upgradeToProDaysLeft', { days: daysLeft }),
        href: '/subscriptions/upgrade',
        color: 'warning'
      };
    }
    
    if (planName === 'Small Business') {
      return {
        text: t('sidebar.upgradeToEnterprise'),
        href: '/subscriptions/upgrade',
        color: 'primary'
      };
    }

    if (planName === 'Professional') {
      return {
        text: t('sidebar.upgradeToEnterprise'),
        href: '/subscriptions/upgrade',
        color: 'primary'
      };
    }

    if (planName === 'Enterprise') {
      return {
        text: t('sidebar.contactSupport'),
        href: 'https://wa.me/923174088510?text=Regarding%20Enterprise%20Subscription',
        color: 'success'
      };
    }

    return { 
      text: t('sidebar.upgradeToPro'), 
      href: '/subscriptions/upgrade', 
      color: 'warning' 
    };
  };

  const closeSidebar = () => toggleSidebar();
  const billingRestricted = isBillingRestricted();
  const buttonConfig = getButtonConfig();

  return (
    <>
      <SidebarWrapper
        sx={{
          display: { xs: 'none', lg: 'flex' },
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
          background: (theme) => (theme as any).sidebar.background,
          border: 'none',
          boxShadow: 'none',
          flexDirection: 'column',
          height: '100vh'
        }}
      >
        {/* Fixed Logo Area */}
        <Box
          sx={{
            height: theme.header.height,
            display: 'flex',
            alignItems: 'center',
            px: 2,
            // Modern: no surface of its own — the page gradient runs behind the logo
            // strip so the sidebar reads as part of one continuous canvas.
            bgcolor: (theme as any).isModern
              ? 'transparent'
              : theme.palette.mode === 'dark' ? '#060D1A' : 'white',
            borderBottom: `1px solid ${(theme as any).isModern ? (theme as any).sidebar.dividerBg : theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : alpha(theme.colors.alpha.black[100], 0.1)}`,
            flexShrink: 0,
            transition: 'background-color 0.3s ease'
          }}
        >
          <Box
            component="img"
            // The sidebar canvas is dark in every modern variant, so use the white logo
            // asset rather than CSS-inverting the dark one.
            src={((theme as any).isModern && !(theme as any).modernPaper) || theme.palette.mode === 'dark'
              ? '/sum_cloud_pos_full_logo_white.png'
              : '/sum_cloud_pos_full_logo.png'}
            alt="Meta Sum Cloud POS"
            sx={{ height: 64, width: 'auto', maxWidth: '100%', objectFit: 'contain' }}
          />
        </Box>

        {/* Scrollable Menu Area */}
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <Scrollbar>
            <Divider
              sx={{
                mx: theme.spacing(2),
                background: (theme) => (theme as any).sidebar.dividerBg
              }}
            />
            <SidebarMenu />
          </Scrollbar>
        </Box>
        
        {/* Fixed Bottom Button Area */}
        <Box sx={{ flexShrink: 0, mt: 'auto' }}>
          <Divider sx={{ background: (theme) => (theme as any).sidebar.dividerBg }} />
          <Box p={2}>
            {loading ? (
              <Button
                fullWidth
                variant="contained"
                sx={{ background: 'linear-gradient(135deg,#E45A0D,#C44A00)', '&:hover': { background: 'linear-gradient(135deg,#E45A0D,#C44A00)', opacity: 0.9 }, boxShadow: '0 4px 14px -4px rgba(228,90,13,0.5)', border: 'none' }}
                disabled
              >
                <CircularProgress size={24} color="inherit" />
              </Button>
            ) : billingRestricted ? (
              <Button
                component={RouterLink}
                to="/management/billing"
                onClick={closeSidebar}
                variant="contained"
                sx={{ background: 'linear-gradient(135deg,#E45A0D,#C44A00)', '&:hover': { background: 'linear-gradient(135deg,#E45A0D,#C44A00)', opacity: 0.9 }, boxShadow: '0 4px 14px -4px rgba(228,90,13,0.5)', border: 'none' }}
                size="small"
                fullWidth
              >
                {t('menu.billing')}
              </Button>
            ) : (
              <Button
                href={buttonConfig.href}
                target={buttonConfig.text === 'Contact Support' ? '_blank' : '_self'}
                rel="noopener noreferrer"
                variant="contained"
                sx={{ background: 'linear-gradient(135deg,#E45A0D,#C44A00)', '&:hover': { background: 'linear-gradient(135deg,#E45A0D,#C44A00)', opacity: 0.9 }, boxShadow: '0 4px 14px -4px rgba(228,90,13,0.5)', border: 'none' }}
                size="small"
                fullWidth
              >
                {buttonConfig.text}
              </Button>
            )}
          </Box>
        </Box>
      </SidebarWrapper>

      <Drawer
        sx={{ boxShadow: `${theme.sidebar.boxShadow}` }}
        anchor={theme.direction === 'rtl' ? 'right' : 'left'}
        open={sidebarToggle}
        onClose={closeSidebar}
        variant="temporary"
        elevation={9}
      >
        <SidebarWrapper
          sx={{
            background: (theme) => (theme as any).sidebar.background,
            border: 'none',
            boxShadow: 'none',
            display: 'flex',
            flexDirection: 'column',
            height: '100vh'
          }}
        >
          {/* Fixed Logo Area */}
          <Box
            sx={{
              height: theme.header.height,
              display: 'flex',
              alignItems: 'center',
              px: 2,
              bgcolor: (theme as any).isModern
                ? 'transparent'
                : theme.palette.mode === 'dark' ? theme.palette.background.paper : 'white',
              borderBottom: `1px solid ${(theme as any).isModern ? (theme as any).sidebar.dividerBg : alpha(theme.colors.alpha.black[100], 0.1)}`,
              flexShrink: 0,
              transition: 'background-color 0.3s ease'
            }}
          >
            <Box
              component="img"
              src={((theme as any).isModern && !(theme as any).modernPaper) || theme.palette.mode === 'dark'
                ? '/sum_cloud_pos_full_logo_white.png'
                : '/sum_cloud_pos_full_logo.png'}
              alt="Meta Sum Cloud POS"
              sx={{ height: 64, width: 'auto', maxWidth: '100%', objectFit: 'contain' }}
            />
          </Box>
          
          {/* Scrollable Menu Area */}
          <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <Scrollbar>
              <Divider
                sx={{
                  mx: theme.spacing(2),
                  background: (theme) => (theme as any).sidebar.dividerBg
                }}
              />
              <SidebarMenu />
            </Scrollbar>
          </Box>
          
          {/* Fixed Bottom Button Area */}
          <Box sx={{ flexShrink: 0, mt: 'auto' }}>
            <Divider sx={{ background: (theme) => (theme as any).sidebar.dividerBg }} />
            <Box p={2}>
              {loading ? (
                <Button
                  fullWidth
                  variant="contained"
                  sx={{ background: 'linear-gradient(135deg,#E45A0D,#C44A00)', '&:hover': { background: 'linear-gradient(135deg,#E45A0D,#C44A00)', opacity: 0.9 }, boxShadow: '0 4px 14px -4px rgba(228,90,13,0.5)', border: 'none' }}
                  disabled
                >
                  <CircularProgress size={24} color="inherit" />
                </Button>
              ) : billingRestricted ? (
                <Button
                  component={RouterLink}
                  to="/management/billing"
                  onClick={closeSidebar}
                  variant="contained"
                  sx={{ background: 'linear-gradient(135deg,#E45A0D,#C44A00)', '&:hover': { background: 'linear-gradient(135deg,#E45A0D,#C44A00)', opacity: 0.9 }, boxShadow: '0 4px 14px -4px rgba(228,90,13,0.5)', border: 'none' }}
                  size="small"
                  fullWidth
                >
                  {t('menu.billing')}
                </Button>
              ) : (
                <Button
                  href={buttonConfig.href}
                  target={buttonConfig.text === t('sidebar.contactSupport') ? '_blank' : '_self'}
                  rel="noopener noreferrer"
                  variant="contained"
                  sx={{ background: 'linear-gradient(135deg,#E45A0D,#C44A00)', '&:hover': { background: 'linear-gradient(135deg,#E45A0D,#C44A00)', opacity: 0.9 }, boxShadow: '0 4px 14px -4px rgba(228,90,13,0.5)', border: 'none' }}
                  size="small"
                  fullWidth
                >
                  {buttonConfig.text}
                </Button>
              )}
            </Box>
          </Box>
        </SidebarWrapper>
      </Drawer>
    </>
  );
}

export default Sidebar;